// web/autoEvolution.ts — AUTO-EVOLUTION MANAGEMENT: player-configurable rules that
// resolve loot automatically so late-game players can open hundreds of boxes without
// inventory management becoming a chore.
//
// This module is PURE + self-contained: it decides a DISPOSITION (equip / sacrifice /
// save / ask) for each reward gene from a normalised `RewardInfo`, and estimates the
// Mutation Energy a ruleset would yield. The CALLER executes the dispositions (equip =
// the existing replay-safe applyGene action; sacrifice = the replay-safe sacrifice
// action; save = keep), so determinism is preserved — the decisions replay as the
// logged actions, and the ruleset itself is a UI preference (never in the save).
//
// Anything the rules DON'T resolve is left as `ask`, which the manual reward card
// (web/ui.ts) presents with EQUIP / SACRIFICE / SAVE so the satisfying choice survives
// for genes the rules don't cover.

import { RARITY, RARITY_COUNT } from '../src/types.ts';
import { genePower } from '../src/sim/genePower.ts';

/** Disposition for one reward gene. */
export const DISPOSITION = {
  Equip: 'equip',
  Sacrifice: 'sacrifice',
  Save: 'save',
  Ask: 'ask',
} as const;
export type Disposition = (typeof DISPOSITION)[keyof typeof DISPOSITION];

/**
 * Mutation Energy yielded when a gene of each rarity is sacrificed. MIRRORS
 * `SACRIFICE_YIELD` in web/evolutionBox.ts (Common…Legendary) — keep in sync; the
 * estimate is only as accurate as this table.
 */
export const SACRIFICE_YIELD: readonly number[] = [3, 6, 12, 24, 48];

// --- Power ------------------------------------------------------------------
// Power Score comes from the CANONICAL genePower model so the rules + estimate share
// the box reveal's scale exactly. The runtime reads each gene's FULL score (expression
// + ascension aware) via `game.geneInfo()`; `powerOf` below is the rarity+traits-only
// approximation used for the DEFAULT threshold + unit tests (no inventory in hand).

/**
 * Power Score for a "clean" gene of `rarity` with `traitTotal` stacked mutations, via
 * the canonical `genePower` weights. Expression / synergy / ascension / splice-tier
 * aren't derivable from rarity+traits alone, so they're zero here — a deliberate
 * lower-bound used only to seed the default threshold + tests; live decisions use the
 * gene's true expression-aware Power from `game.geneInfo().power`.
 */
export function powerOf(rarity: number, traitTotal: number): number {
  return genePower({
    rarity,
    mutationCount: traitTotal,
    synergyCount: 0,
    upgradeLevel: 0,
    evolutionTier: 0,
    exprPower: 0,
  });
}

/** A normalised reward the rules reason about (built by the caller from the gene). */
export interface RewardInfo {
  /** Gene type id (Wing/Mandible/…). */
  readonly type: number;
  readonly rarity: number;     // RARITY.* (0..4)
  readonly traitTotal: number; // stacked mutations
  readonly power: number;      // powerOf(rarity, traitTotal) — or the canonical score
  /** True if the player holds ANOTHER gene of the same (type) already. */
  readonly isDuplicate: boolean;
  /** True if this is a strict power upgrade over the best same-type gene held. */
  readonly isUpgrade: boolean;
}

/** The player's auto-resolution ruleset. */
export interface AutoRules {
  /** Master switch — off ⇒ every reward is `ask` (the manual card, unchanged). */
  enabled: boolean;
  equipUpgrades: boolean;
  sacrificeDuplicates: boolean;  // lower-power duplicates only
  saveLegendaries: boolean;      // rarity Legendary
  saveApex: boolean;             // rarity Epic+ (apex-grade)
  sacrificeCommons: boolean;
  sacrificeBelowPower: boolean;
  belowPowerX: number;
  /** Rewards no rule matched: false ⇒ `ask` (manual card); true ⇒ silently saved. */
  keepRest: boolean;
}

export const DEFAULT_AUTO_RULES: AutoRules = {
  enabled: false,
  equipUpgrades: true,
  sacrificeDuplicates: true,
  saveLegendaries: true,
  saveApex: true,
  sacrificeCommons: false,
  sacrificeBelowPower: false,
  belowPowerX: powerOf(RARITY.Uncommon, 0), // 200 — below a clean Uncommon
  keepRest: false,
};

/**
 * Decide one reward's disposition. Priority (high → low): protect value first
 * (equip an upgrade, save legendaries/apex), THEN cull (duplicates, commons, weak),
 * else fall through to the manual card (or a silent save when keepRest is on).
 */
export function decide(info: RewardInfo, rules: AutoRules): Disposition {
  if (!rules.enabled) return DISPOSITION.Ask;
  if (rules.equipUpgrades && info.isUpgrade) return DISPOSITION.Equip;
  if (rules.saveLegendaries && info.rarity >= RARITY.Legendary) return DISPOSITION.Save;
  if (rules.saveApex && info.rarity >= RARITY.Epic) return DISPOSITION.Save;
  // A duplicate that ISN'T an upgrade is dead weight → convert it.
  if (rules.sacrificeDuplicates && info.isDuplicate && !info.isUpgrade) return DISPOSITION.Sacrifice;
  if (rules.sacrificeCommons && info.rarity <= RARITY.Common) return DISPOSITION.Sacrifice;
  if (rules.sacrificeBelowPower && info.power < rules.belowPowerX) return DISPOSITION.Sacrifice;
  return rules.keepRest ? DISPOSITION.Save : DISPOSITION.Ask;
}

/** Energy a single sacrificed gene of this rarity yields (clamped). */
export function sacrificeYield(rarity: number): number {
  const r = rarity < 0 ? 0 : rarity >= RARITY_COUNT ? RARITY_COUNT - 1 : rarity;
  return SACRIFICE_YIELD[r] as number;
}

export interface RuleSummary {
  equip: number;
  sacrifice: number;
  save: number;
  ask: number;
  /** Estimated Mutation Energy from the sacrifices. */
  energy: number;
}

/** Roll a batch of rewards through the rules → per-disposition counts + energy. */
export function summarize(infos: readonly RewardInfo[], rules: AutoRules): RuleSummary {
  const s: RuleSummary = { equip: 0, sacrifice: 0, save: 0, ask: 0, energy: 0 };
  for (const info of infos) {
    const d = decide(info, rules);
    if (d === DISPOSITION.Equip) s.equip++;
    else if (d === DISPOSITION.Sacrifice) { s.sacrifice++; s.energy += sacrificeYield(info.rarity); }
    else if (d === DISPOSITION.Save) s.save++;
    else s.ask++;
  }
  return s;
}

/** Estimated Mutation Energy a ruleset would reap from these rewards. */
export function estimateEnergy(infos: readonly RewardInfo[], rules: AutoRules): number {
  return summarize(infos, rules).energy;
}

// --- Persistence (a UI preference — NEVER part of the deterministic save) -----

const RULES_KEY = 'brood.autorules.v1';

function storage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    const k = '__brood_autorules_probe__';
    s.setItem(k, '1');
    s.removeItem(k);
    return s;
  } catch {
    return null;
  }
}

export function loadAutoRules(): AutoRules {
  const s = storage();
  if (!s) return { ...DEFAULT_AUTO_RULES };
  const raw = s.getItem(RULES_KEY);
  if (raw === null) return { ...DEFAULT_AUTO_RULES };
  try {
    const parsed = JSON.parse(raw) as Partial<AutoRules>;
    if (typeof parsed !== 'object' || parsed === null) return { ...DEFAULT_AUTO_RULES };
    return { ...DEFAULT_AUTO_RULES, ...parsed };
  } catch {
    return { ...DEFAULT_AUTO_RULES };
  }
}

export function saveAutoRules(rules: AutoRules): void {
  const s = storage();
  if (!s) return;
  try { s.setItem(RULES_KEY, JSON.stringify(rules)); } catch { /* quota/disabled — ignore */ }
}

// --- view (HTML builders for the config panel + the manual resolution card) ---

import { RARITY_NAME } from '../src/sim/geneData.ts';
import { statLinesHTML } from './geneCard.ts';

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;'));
}

/** One reward awaiting a decision (the prominent manual card). */
export interface ResolveCard {
  geneId: number;
  serial: number;
  name: string;        // describeGene label
  type: number;
  rarity: number;
  /** Stacked mutations — feeds the collectible stat lines on the resolve card. */
  traitTotal: number;
  power: number;
  /** Best same-type gene currently held (excl. this one); currentPower < 0 ⇒ none. */
  currentLabel: string;
  currentPower: number;
  /** Energy this gene yields if sacrificed. */
  sacrificeEnergy: number;
  /** Display name of the caste EQUIP targets (the gene type's natural caste). */
  equipCasteName: string;
}

/** The prominent reward card with the three single-click choices. */
export function resolveCardHTML(c: ResolveCard, remaining: number): string {
  const hasCurrent = c.currentPower >= 0;
  const delta = hasCurrent ? c.power - c.currentPower : 0;
  const deltaCls = !hasCurrent ? 'new' : delta > 0 ? 'up' : delta < 0 ? 'down' : 'same';
  const deltaStr = !hasCurrent ? 'NEW TYPE' : `${delta > 0 ? '+' : ''}${delta} power`;
  const more = remaining > 1 ? `<span class="rs-more">${remaining - 1} more to resolve</span>` : '';
  return (
    `<div class="rs-card rcol-${c.rarity} rarity-${c.rarity}">` +
      `<div class="rs-head"><span class="rs-name">${esc(c.name)}</span>` +
      `<span class="rs-rarity r-${c.rarity}">${esc(RARITY_NAME[c.rarity] ?? '?')}</span></div>` +
      `<div class="rs-power">Power <b>${c.power}</b> <span class="rs-delta ${deltaCls}">${deltaStr}</span></div>` +
      statLinesHTML(c.type, c.rarity, c.traitTotal) +
      `<div class="rs-current">${hasCurrent
        ? `Current best: ${esc(c.currentLabel)} <span class="muted">(power ${c.currentPower})</span>`
        : `<span class="muted">You hold no gene of this type yet.</span>`}</div>` +
      `<div class="rs-actions">` +
        `<button class="btn btn-primary rs-equip" data-act="equip">⚡ Equip Now → ${esc(c.equipCasteName)}</button>` +
        `<button class="btn rs-sacrifice" data-act="sacrifice">♻ Sacrifice <span class="muted">+${c.sacrificeEnergy}</span></button>` +
        `<button class="btn btn-ghost rs-save" data-act="save">💾 Store</button>` +
      `</div>` +
      more +
    `</div>`
  );
}

/** The post-open auto-actions summary line. */
export function autoSummaryHTML(s: RuleSummary): string {
  if (s.equip + s.sacrifice + s.save === 0) return 'Rewards added to your inventory.';
  const parts: string[] = [];
  if (s.equip) parts.push(`⚡ equipped ${s.equip}`);
  if (s.sacrifice) parts.push(`♻ sacrificed ${s.sacrifice} (+${s.energy})`);
  if (s.save) parts.push(`💾 saved ${s.save}`);
  return `Auto-Evolution: ${parts.join(' · ')}.`;
}

/** The auto-rules config form (ids `ar-*`; the caller reads them on change). */
export function autoRulesFormHTML(rules: AutoRules): string {
  const chk = (id: string, on: boolean, label: string): string =>
    `<label class="ar-row"><input type="checkbox" id="${id}"${on ? ' checked' : ''} /> ${label}</label>`;
  return (
    chk('ar-enabled', rules.enabled, '<b>Enable auto-evolution</b> — resolve loot automatically') +
    `<div class="ar-group">` +
      chk('ar-equip', rules.equipUpgrades, "Auto-equip upgrades (each to its gene's natural caste)") +
      chk('ar-save-leg', rules.saveLegendaries, 'Auto-save Legendaries') +
      chk('ar-save-apex', rules.saveApex, 'Auto-save Apex genes (Epic+)') +
      chk('ar-sac-dup', rules.sacrificeDuplicates, 'Auto-sacrifice lower-power duplicates') +
      chk('ar-sac-com', rules.sacrificeCommons, 'Auto-sacrifice Commons') +
      chk('ar-sac-pow', rules.sacrificeBelowPower,
        `Auto-sacrifice anything below <input type="number" id="ar-pow" min="0" max="9999" value="${rules.belowPowerX}" /> power`) +
      chk('ar-keep-rest', rules.keepRest, 'Silently SAVE anything no rule matched (else show the card)') +
    `</div>`
  );
}
